import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, Loader2, ChevronRight, Heart, PartyPopper } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function ShoutoutsWidget() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [shoutouts, setShoutouts] = useState([]);

  useEffect(() => {
    fetchShoutouts();
  }, []);

  const fetchShoutouts = async () => {
    try {
      const res = await apiRequest('/recognition/shoutouts?limit=3');
      setShoutouts(res.shoutouts || []);
    } catch (err) {
      console.error('Failed to load shoutouts');
    } finally {
      setLoading(false);
    }
  };

  const getReactionCount = (shoutout) => {
    if (!shoutout.reactions) return 0;
    let count = 0;
    Object.values(shoutout.reactions).forEach(arr => count += arr.length);
    return count;
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="flex items-center justify-center h-48">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="shoutouts-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Megaphone className="text-orange-500" size={18} />
            Shout-outs
          </span>
          <Button variant="ghost" size="sm" onClick={() => navigate('/gamification')}>
            <ChevronRight size={16} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {shoutouts.length > 0 ? (
          <div className="space-y-3">
            {shoutouts.map((shoutout) => (
              <div
                key={shoutout.id}
                className="p-3 rounded-lg bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-100"
              >
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-full bg-orange-200 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {shoutout.user_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="font-medium text-sm">{shoutout.user_name}</span>
                      {shoutout.badge_info && (
                        <span className="text-sm">{shoutout.badge_info.emoji}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-2 mt-1">
                      {shoutout.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      {getReactionCount(shoutout) > 0 && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Heart size={10} className="text-red-400" />
                          {getReactionCount(shoutout)}
                        </span>
                      )}
                      <span className="text-xs text-slate-400">
                        by {shoutout.created_by_name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-slate-500 text-sm">
            <PartyPopper size={32} className="mx-auto mb-2 opacity-30" />
            <p>No shout-outs yet</p>
            <Button 
              variant="link" 
              size="sm" 
              onClick={() => navigate('/gamification')}
              className="text-orange-600"
            >
              Be the first to recognize someone!
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
